export interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  accent_text_color?: string;
  section_bg_color?: string;
  section_header_text_color?: string;
  subtitle_text_color?: string;
  destructive_text_color?: string;
  bottom_bar_bg_color?: string;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
    };
    start_param?: string;
    auth_date?: number;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: TelegramThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight?: number;
  ready: () => void;
  expand: () => void;
  close: () => void;
  openTelegramLink?: (url: string) => void;
  isVersionAtLeast: (version: string) => boolean;
  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
  requestFullscreen?: () => void;
  lockOrientation?: () => void;
  unlockOrientation?: () => void;
  requestChat?: (preparedButtonId: string, callback?: (ok: boolean) => void) => void;
  shareMessage?: (preparedMessageId: string, callback?: (ok: boolean) => void) => void;
  switchInlineQuery?: (query: string, chooseChatTypes?: string[]) => void;
  requestWriteAccess?: (callback?: (granted: boolean) => void) => void;
  addToHomeScreen?: () => void;
  checkHomeScreenStatus?: (callback: (status: 'unsupported' | 'unknown' | 'added' | 'missed') => void) => void;
  safeAreaInset?: { top: number; bottom: number; left: number; right: number };
  contentSafeAreaInset?: { top: number; bottom: number; left: number; right: number };
  onEvent?: (eventType: string, eventHandler: (...args: unknown[]) => void) => void;
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  MainButton: {
    text: string;
    isVisible: boolean;
    isActive: boolean;
    isProgressVisible: boolean;
    setText: (text: string) => void;
    onClick: (fn: () => void) => void;
    offClick: (fn: () => void) => void;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
    setParams: (params: { text?: string; color?: string; text_color?: string; is_active?: boolean; is_visible?: boolean }) => void;
  };
  SecondaryButton?: {
    setText: (text: string) => void;
    show: () => void;
    hide: () => void;
    onClick: (fn: () => void) => void;
    offClick: (fn: () => void) => void;
  };
  CloudStorage?: {
    setItem: (key: string, value: string, callback?: (error: Error | null, ok?: boolean) => void) => void;
    getItem: (key: string, callback: (error: Error | null, value?: string) => void) => void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

export const FALLBACK_THEME: TelegramThemeParams = {
  bg_color: '#0e1621',
  text_color: '#f5f5f5',
  hint_color: '#8b9aa8',
  link_color: '#6ab2f2',
  button_color: '#3390ec',
  button_text_color: '#ffffff',
  secondary_bg_color: '#17212b',
  header_bg_color: '#17212b',
  accent_text_color: '#6ab2f2',
  section_bg_color: '#17212b',
  section_header_text_color: '#6ab2f2',
  subtitle_text_color: '#8b9aa8',
  destructive_text_color: '#e05d5d',
  bottom_bar_bg_color: '#17212b',
};

export function webApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function bootTelegram(): TelegramWebApp | null {
  const app = webApp();
  if (!app) {
    applyTheme(FALLBACK_THEME);
    return null;
  }
  app.ready();
  app.expand();
  applyTheme(app.themeParams);
  try {
    app.setHeaderColor(app.themeParams.header_bg_color ?? app.themeParams.secondary_bg_color ?? FALLBACK_THEME.header_bg_color!);
    app.setBackgroundColor(app.themeParams.bg_color ?? FALLBACK_THEME.bg_color!);
  } catch {
    // Older clients reject unknown colors; the CSS fallback still applies.
  }

  // Insets arrive asynchronously and can change after expand(), rotation, or
  // Telegram UI transitions. Keep CSS variables in sync instead of taking a
  // single startup snapshot. We deliberately do not request fullscreen here:
  // the app does not need an immersive surface and Telegram's normal expanded
  // mode keeps the system status area predictable on mobile devices.
  const syncLayout = () => applyTelegramLayout(app);
  syncLayout();
  app.onEvent?.('viewportChanged', syncLayout);
  app.onEvent?.('safeAreaChanged', syncLayout);
  app.onEvent?.('contentSafeAreaChanged', syncLayout);
  return app;
}

function applyTelegramLayout(app: TelegramWebApp): void {
  const root = document.documentElement;
  const safe = app.safeAreaInset;
  const contentSafe = app.contentSafeAreaInset;

  if (safe) setInsetVariables(root, '--tg-safe', safe);
  if (contentSafe) setInsetVariables(root, '--tg-content-safe', contentSafe);

  const height = app.viewportStableHeight ?? app.viewportHeight;
  if (Number.isFinite(height) && height > 0) {
    root.style.setProperty('--tg-viewport-height', `${height}px`);
  }
}

function setInsetVariables(
  root: HTMLElement,
  prefix: string,
  inset: { top: number; bottom: number; left: number; right: number },
): void {
  root.style.setProperty(`${prefix}-top`, `${Math.max(0, inset.top)}px`);
  root.style.setProperty(`${prefix}-bottom`, `${Math.max(0, inset.bottom)}px`);
  root.style.setProperty(`${prefix}-left`, `${Math.max(0, inset.left)}px`);
  root.style.setProperty(`${prefix}-right`, `${Math.max(0, inset.right)}px`);
}

export function requestTelegramChat(preparedButtonId: string): Promise<boolean> {
  const app = webApp();
  if (!app?.isVersionAtLeast('9.6') || !app.requestChat) return Promise.resolve(false);
  return new Promise((resolve) => {
    try { app.requestChat!(preparedButtonId, resolve); } catch { resolve(false); }
  });
}

export function requestWriteAccess(): Promise<boolean> {
  const app = webApp();
  if (!app?.initData || !app.isVersionAtLeast('6.9') || !app.requestWriteAccess) return Promise.resolve(false);
  return new Promise((resolve) => {
    try { app.requestWriteAccess!(resolve); } catch { resolve(false); }
  });
}

let homeOfferMade = false;
export function offerHomeScreen(): void {
  if (homeOfferMade) return;
  const app = webApp();
  if (!app?.isVersionAtLeast('8.0') || !app.addToHomeScreen || !app.checkHomeScreenStatus) return;
  app.checkHomeScreenStatus((status) => {
    if (status === 'unknown' || status === 'missed') {
      homeOfferMade = true;
      app.addToHomeScreen!();
    }
  });
}

export function sharePreparedMessage(id: string, fallbackQuery: string): void {
  const app = webApp();
  if (app?.isVersionAtLeast('8.0') && app.shareMessage) app.shareMessage(id);
  else app?.switchInlineQuery?.(fallbackQuery, ['users', 'groups', 'channels']);
}

export function applyTheme(params: TelegramThemeParams): void {
  const merged = { ...FALLBACK_THEME, ...params };
  const root = document.documentElement;
  const map: Record<string, string | undefined> = {
    '--tg-bg': merged.bg_color,
    '--tg-text': merged.text_color,
    '--tg-hint': merged.hint_color,
    '--tg-link': merged.link_color,
    '--tg-button': merged.button_color,
    '--tg-button-text': merged.button_text_color,
    '--tg-secondary': merged.secondary_bg_color,
    '--tg-header': merged.header_bg_color,
    '--tg-accent': merged.accent_text_color,
    '--tg-section': merged.section_bg_color,
    '--tg-section-header': merged.section_header_text_color,
    '--tg-subtitle': merged.subtitle_text_color,
    '--tg-destructive': merged.destructive_text_color,
    '--tg-bottom': merged.bottom_bar_bg_color,
  };
  for (const [key, value] of Object.entries(map)) {
    if (value) {
      root.style.setProperty(key, value);
    }
  }
}

export function haptic(kind: 'light' | 'success' | 'error' | 'warning' = 'light'): void {
  const fb = webApp()?.HapticFeedback;
  if (!fb) {
    return;
  }
  if (kind === 'light') {
    fb.impactOccurred('light');
  } else {
    fb.notificationOccurred(kind);
  }
}

export function persist(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode.
  }
  const app = webApp();
  if (app?.initData && app.isVersionAtLeast('6.9')) {
    try { app.CloudStorage?.setItem(key, value); } catch { /* Local fallback is already saved. */ }
  }
}

export function readPersisted(key: string): Promise<string | null> {
  const local = () => { try { return localStorage.getItem(key); } catch { return null; } };
  const app = webApp();
  const cloud = app?.initData && app.isVersionAtLeast('6.9') ? app.CloudStorage : null;
  if (cloud) {
    return new Promise((resolve) => {
      const timer = window.setTimeout(() => resolve(local()), 3000);
      try { cloud.getItem(key, (error, value) => {
        window.clearTimeout(timer);
        resolve(error ? local() : (value || local()));
      }); } catch { window.clearTimeout(timer); resolve(local()); }
    });
  }
  return Promise.resolve(local());
}
